import React, { useEffect, useState } from "react";
import api from "../utils/api";
import { useParams } from "react-router-dom";
const headerImg = "/header.png";

export default function ExtruderPrint() {
  const { id } = useParams();

  const [header, setHeader] = useState(null);
  const [lines, setLines] = useState([]);
  const [readyToPrint, setReadyToPrint] = useState(false);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setReadyToPrint(false);

        const headersRes = await api.get(`/api/production/headers`);
        const one = (headersRes.data || []).find(
          (x) => String(x.headerId) === String(id)
        );

        const linesRes = await api.get(`/api/production/headers/${id}/lines`);

        if (!alive) return;

        setHeader(one || null);
        setLines(Array.isArray(linesRes.data) ? linesRes.data : []);
        setReadyToPrint(true);
      } catch (e) {
        alert(
          e?.response?.data?.message ||
            e?.response?.data?.error ||
            e.message ||
            "Print load failed"
        );
      }
    })();

    return () => {
      alive = false;
    };
  }, [id]);

  useEffect(() => {
    if (!readyToPrint || !header) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
      });
    });
  }, [readyToPrint, header]);

  if (!header) return <div style={{ padding: 20 }}>Loading...</div>;

  const grandTotal = lines.reduce(
    (sum, r) => sum + Number(r.totalQty ?? r.total_qty ?? 0),
    0
  );

  return (
    <>
      <style>{`
        @page {
          size: A4 portrait;
          margin: 10mm;
        }

        html, body {
          margin: 0;
          padding: 0;
          background: #fff;
          font-family: Arial, sans-serif;
          color: #000;
        }

        .print-page {
          width: 190mm;
          margin: 0 auto;
          padding-bottom: 18mm;
          box-sizing: border-box;
          position: relative;
          background: #fff;
        }

        .company-header {
          width: 190mm;
          height: 32mm;
          margin: 0 auto 6px auto;
          position: relative;
          text-align: center;
          overflow: hidden;
        }

        .company-header-img {
          width: 190mm;
          height: 32mm;
          object-fit: contain;
          display: block;
          margin: 0 auto;
        }

        .report-title {
          text-align: center;
          font-size: 16px;
          font-weight: 700;
          margin: 4px 0 8px 0;
        }

        .close-btn {
          position: absolute;
          right: 0;
          top: 0;
          background: #dc3545;
          color: #fff;
          border: none;
          padding: 6px 12px;
          font-size: 12px;
          border-radius: 4px;
          cursor: pointer;
        }

        .info-box {
          width: 190mm;
          display: flex;
          justify-content: space-between;
          border: 1px solid #000;
          margin: 0 auto 8px auto;
          box-sizing: border-box;
        }

        .info-box > div {
          flex: 1;
          padding: 7px 8px;
          box-sizing: border-box;
        }

        .lbl {
          font-size: 10px;
          margin-bottom: 5px;
        }

        .val {
          font-size: 13px;
          font-weight: 700;
        }

        .print-table {
          width: 190mm;
          margin: 0 auto;
          border-collapse: collapse;
          table-layout: fixed;
        }

        .print-table th,
        .print-table td {
          border: 1px solid #000;
          padding: 5px 4px;
          font-size: 11px;
          vertical-align: top;
          text-align: left;
          box-sizing: border-box;
        }

        .print-table th {
          font-weight: 700;
        }

        .bold {
          font-weight: 700;
          text-align: right;
        }

        .wrap {
          white-space: normal;
          word-break: break-word;
          line-height: 1.35;
        }

        .print-footer {
          position: fixed;
          bottom: 8mm;
          left: 10mm;
          right: 10mm;
          width: auto;
          border-top: 1px solid #000;
          padding-top: 8px;
          display: flex;
          justify-content: space-between;
          box-sizing: border-box;
          background: #fff;
        }

        .print-footer > div {
          width: 45%;
        }

        .footer-label {
          font-size: 10px;
          font-weight: 600;
          margin-bottom: 6px;
        }

        .footer-value {
          font-size: 16px;
          font-weight: 700;
          margin-top: 6px;
        }

        .footer-right {
          text-align: right;
        }

        @media print {
          .no-print {
            display: none !important;
          }

          * {
            box-shadow: none !important;
            background-image: none !important;
          }
        }
      `}</style>

      <div className="print-page">
        <div className="company-header">
          <img
            src={headerImg}
            alt="Saudi Mais Header"
            className="company-header-img"
          />

          <button
            type="button"
            className="close-btn no-print"
            onClick={() => {
              window.close();
              setTimeout(() => {
                window.location.href = "/extruder";
              }, 300);
            }}
          >
            ✕ Close
          </button>
        </div>

        <div className="report-title">DAILY EXTRUDER DEPARTMENT PRODUCTION</div>

        <div className="info-box">
          <div>
            <div className="lbl">Entry ID</div>
            <div className="val">{header.headerId}</div>
          </div>

          <div>
            <div className="lbl">Date</div>
            <div className="val">
              {String(header.productionDate || "").slice(0, 10)}
            </div>
          </div>

          <div>
            <div className="lbl">Total Qty</div>
            <div className="val">{grandTotal.toFixed(2)}</div>
          </div>
        </div>

        <table className="print-table">
          <thead>
            <tr>
              <th>Sr</th>
              <th>Machine</th>
              <th>Item Code</th>
              <th>Item Name</th>
              <th>Unit</th>
              <th>Quantity</th>
              <th>Total</th>
            </tr>
          </thead>

          <tbody>
            {lines.map((r, i) => (
              <tr key={r.lineId ?? i}>
                <td>{i + 1}</td>
                <td>{r.machineName ?? r.machineNo ?? r.machine_name ?? "-"}</td>
                <td>{r.itemCode ?? r.item_code ?? "-"}</td>
                <td className="wrap">
                  {r.itemName ?? r.item_name ?? r.productName ?? "-"}
                </td>
                <td>{r.unit ?? "-"}</td>
                <td>{r.quantityExpr ?? r.quantity_expr ?? "-"}</td>
                <td className="bold">
                  {Number(r.totalQty ?? r.total_qty ?? 0).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="print-footer">
          <div>
            <div className="footer-label">Production Manager</div>
            <div className="footer-value">{header.checkedBy || ""}</div>
          </div>

          <div className="footer-right">
            <div className="footer-label">Extruder In Charge</div>
            <div className="footer-value">{header.receivedBy || ""}</div>
          </div>
        </div>
      </div>
    </>
  );
}